
import { of as observableOf, from as observableFrom,  Observable ,  Subject } from 'rxjs'

import { mergeMap,  catchError } from 'rxjs/operators'
import { useState, useRef, useEffect } from 'react'
import { styled } from '@mui/material/styles'
import Dialog from '@mui/material/Dialog'
import { MyFile, PHOTO_TYPE, VIDEO_TYPE, GIF_TYPE, FileFolder } from 'interfaces'
import { NativeVideoPlayer } from 'components/VideoPlayer'
import GifPlayer from 'components/GifPlayer'
import DeleteIcon from '@mui/icons-material/Delete'
import FolderIcon from '@mui/icons-material/Folder'
import Button from '@mui/material/Button'
import { bytesToSize } from 'shared'
import { FormattedMessage, useIntl } from 'react-intl'
import ProgressMedia from 'components/Card/ProgressMedia'
import Tooltip from '@mui/material/Tooltip'

const Clipboard = require('clipboard')
const cssVar = require('styles/variables')
import styles from './FileDialog.pcss'
import SimpleLoader from 'components/SimpleLoader'
import { StoreThunkDispatch } from 'store/state'
import { useDispatch } from 'react-redux'
import { deleteFile, getStorageInfo } from 'services/uploads/actions'
import { action, message } from 'services/snackbar'
import { HTTP_STATUS_BAD_REQUEST } from 'services/net'
import { NOTIFICATION_DURATION_LONG } from 'components/ConnectedSnackbar'
import { makeStyles } from '@mui/styles'

const PREFIX = 'FileDialog'

const classes = {
  root: `${PREFIX}-root`
}
const StyledDialog = styled(Dialog)({
  [`& .${classes.root}`]: {
    fontSize: '1.2rem',
    color: cssVar['--alert-red'],
    margin: '0 0 0 4px',

    '&:hover': {
      backgroundColor: cssVar['--pp-black-38']
    }
  }
})

const makeDeleteButtonStyles = makeStyles({
  [`& .${classes.root}`]: {
    fontSize: '1.2rem',
    color: cssVar['--alert-red'],
    margin: '0 0 0 4px',

    '&:hover': {
      backgroundColor: cssVar['--pp-black-38']
    }
  }
})

export interface FileDialogProps {
  file?: MyFile
  folder?: FileFolder
  setFileInPreview: (file?: MyFile) => void
}

export function FileDialog(props: FileDialogProps) {
  const intl = useIntl()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const [fileDeleteLoading, setFileDeleteLoading] = useState(false)
  const [urlCopied, setUrlCopied] = useState(false)
  const [imageLoading, setImageLoading] = useState(true)
  const file = props.file
  const prettySize = bytesToSize(file?.size || 0)
  const deleteBtnClasses = makeDeleteButtonStyles()
  const clipboard = useRef(null)
  const delete$ = useRef<Subject<{ file: MyFile, force: boolean }>>(undefined)

  useEffect(() => {
    delete$.current = new Subject()
    delete$.current.pipe(
      mergeMap(({ file, force }) => {
        return observableFrom(dispatch(deleteFile({ file, force })).unwrap())
          .pipe(catchError(response => {
            if (response.status === HTTP_STATUS_BAD_REQUEST && response.response.msg === 'File used in posts.') {
              dispatch(action(
                'warning',
                intl.formatMessage({ id: 'find.my-uploads.messages.confirm-delete-used-file' }),
                intl.formatMessage({ id: 'actions.delete' }),
                () => {
                  delete$.current?.next({ file, force: true })
                },
                NOTIFICATION_DURATION_LONG
              ))
              return observableOf({ error: response, handled: true })
            }
            return observableOf({ error: response })
          })).pipe(
            mergeMap((response: any) => {
              if (!response.error) {
                return dispatch(getStorageInfo())
              }
              return observableOf(response)
            }))
      }))
      .subscribe((result) => {
        if (result.error && !result.handled) {
          dispatch(message(intl.formatMessage({ id: 'errors.generic' }), 'error'))
        }
        setFileDeleteLoading(false)
      })
  }, [dispatch, intl])

  const setPathElementRef = (element: HTMLElement | null) => {
    if (file && element) {
      if (clipboard.current) {
        (clipboard.current as any).destroy()
      }

      clipboard.current = new Clipboard(element)
      ; (clipboard.current as any).on('success', () => {
        setUrlCopied(true)
      })
      ; (clipboard.current as any).on('error', (e: any) => {
        console.log('Clipboard error', e)
        setUrlCopied(false)
      })
    }
  }

  const onUrlTooltipOpen = () => {
    setUrlCopied(false)
  }

  const close = () => {
    props.setFileInPreview(undefined)
  }

  const stopImageLoading = () => {
    setImageLoading(false)
  }

  const onDeleteFile = () => {
    if (file) {
      setFileDeleteLoading(true)
      delete$.current?.next({ file, force: false })
      close()
    }
  }

  const pathname = `${props.folder?.name} / ${file?.name}`
  const fixedFooterClass = file?.type === VIDEO_TYPE ? styles['footer-fixed'] : ''

  return (
    <StyledDialog
      open={Boolean(file)}
      onClose={close}
      classes={{ paper: styles.dialog }}
      className={fixedFooterClass}
      disableEnforceFocus
      data-testid="file-dialog"
    >
      {
        file && imageLoading && (
          <ProgressMedia
            image={file.url}
            loader={<SimpleLoader className={styles.loader} />}
            className={styles['loader-box']}
            onDone={stopImageLoading}
            onError={stopImageLoading}
          />
        )
      }
      {
        file && !imageLoading && (
          <div className={styles.content}>
            {
              file.type === PHOTO_TYPE && (
                <img src={file.url} className={styles.image} />
              )
            }
            {
              file.type === VIDEO_TYPE && (
                <NativeVideoPlayer
                  controls
                  videoUrl={file.url}
                  className={styles['video-player']}
                  videoElementClassName={styles.video}
                />
              )
            }
            {
              file.type === GIF_TYPE && (
                <GifPlayer
                  loop
                  gifUrl={file.url}
                />
              )
            }
          </div>
        )
      }
      <footer className={styles.footer}>
        <Tooltip
          placement="top"
          title={<FormattedMessage id={urlCopied ? 'notifications.generic-copied' : 'uploads.tooltip.copy-file-url'} />}
          onOpen={onUrlTooltipOpen}
        >
          <p
            className={styles.path}
            data-clipboard-text={file?.url || ''}
            ref={setPathElementRef}
          >
            <FolderIcon className={styles['folder-icon']} />
            <span className="text-ellipsis" title={pathname}>{pathname}</span>
            <span className={styles.filesize}>{` (${prettySize})`}</span>
          </p>
        </Tooltip>
        <Button
          startIcon={<DeleteIcon />}
          classes={deleteBtnClasses}
          disabled={fileDeleteLoading}
          onClick={onDeleteFile}
        >
          <FormattedMessage id="actions.delete" />
        </Button>
      </footer>
    </StyledDialog>
  )
}
